<template>
  <div class="checkout-page pb-12">
    <ServerError v-if="ServerError" />
    <PageLoader v-bind:storage="appLoading" v-bind:contain="true" />

    <v-snackbar v-model="showSnackBar" color="#3EA56E" right timeout="2000" class="mt-12">
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
        <span class="ibmmedium">{{ msg }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="showSnackBar = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <v-container>
      <v-layout wrap>
        <!-- Page Header -->
        <v-flex xs12 class="mb-8">
          <div class="d-flex align-center">
            <v-btn icon @click="$router.push('/cart')" class="mr-2">
              <v-icon color="#3EA56E">mdi-arrow-left</v-icon>
            </v-btn>
            <h1 class="page-title">Confirm Checkout</h1>
          </div>
          <v-divider class="title-divider mt-2"></v-divider>
        </v-flex>

        <v-layout wrap v-if="user || cart.length > 0">
          <!-- Left Column: Shipping & Promocode -->
          <v-flex xs12 lg7 class="pr-lg-6">
            <!-- Shipping Card -->
            <v-card flat class="confirm-card mb-6 pa-6">
              <div class="d-flex align-center justify-space-between mb-4">
                <div class="d-flex align-center">
                  <v-icon color="#3EA56E" class="mr-3">mdi-truck-delivery-outline</v-icon>
                  <h3 class="section-subtitle">Shipping Address</h3>
                </div>
                <v-btn text color="#3EA56E" class="text-none ibmsemibold" to="/cart">
                  Change
                </v-btn>
              </div>
              
              <div class="address-confirmation-block pa-4 rounded-xl">
                <div v-if="user">
                  <h4 class="customer-name mb-2">{{ user.firstname }} {{ user.lastname }}</h4>
                  <p class="address-text mb-1">
                    {{ user.streetAddress }}, {{ user.locality }}, {{ user.city }}
                  </p>
                  <p class="address-text mb-1">
                    {{ user.state }}, {{ user.country }} - {{ user.pincode }}
                  </p>
                  <p class="address-text ibmmedium color-dark mt-2">
                    <v-icon small class="mr-1">mdi-phone-outline</v-icon>
                    {{ user.delphone }}
                  </p>
                </div>
                <div v-else class="text-center py-4">
                  <span class="ibmmedium grey--text">No address selected</span>
                </div>
              </div>
            </v-card>

            <!-- Promocode Card -->
            <v-card flat class="confirm-card mb-6 pa-6" v-if="codes && codes.length > 0">
              <div class="d-flex align-center mb-4">
                <v-icon color="#3EA56E" class="mr-3">mdi-ticket-percent-outline</v-icon>
                <h3 class="section-subtitle">Apply Promocode</h3>
              </div>
              
              <div class="promo-input-wrapper d-flex align-center">
                <v-text-field
                  v-model="code1"
                  placeholder="Enter code"
                  outlined
                  dense
                  hide-details
                  class="custom-field flex-grow-1"
                  color="#3EA56E"
                ></v-text-field>
                <v-btn
                  depressed
                  color="#3EA56E"
                  dark
                  class="ml-3 px-8 rounded-lg ibmsemibold"
                  height="40"
                  @click="applyPromocode()"
                  :disabled="!code1"
                >
                  Apply
                </v-btn>
              </div>

              <!-- Available Coupons -->
              <div class="available-coupons mt-4">
                <span class="ibmmedium grey--text text-caption mb-2 d-block">Available Coupons</span>
                <v-layout wrap>
                  <v-flex xs12 v-for="(item, i) in codes" :key="i" class="mb-2">
                    <div class="coupon-tag d-flex align-center justify-space-between pa-3 rounded-lg border-dashed">
                      <div class="d-flex flex-column">
                        <span class="coupon-code ibmsemibold mb-1">{{ item.promoCode }}</span>
                        <span class="coupon-desc">{{ item.description }}</span>
                      </div>
                      <v-btn text small color="#3EA56E" class="ibmsemibold" @click="callPromo(item.promoCode)">
                        Use Code
                      </v-btn>
                    </div>
                  </v-flex>
                </v-layout>
              </div>
            </v-card>

            <!-- Cart Preview Card -->
            <v-card flat class="confirm-card mb-6 mb-lg-0 pa-6">
              <div class="d-flex align-center mb-6">
                <v-icon color="#3EA56E" class="mr-3">mdi-cart-check</v-icon>
                <h3 class="section-subtitle">Order Preview ({{ cartCount }} items)</h3>
              </div>
              
              <div class="checkout-items-list">
                <div v-for="(item, i) in cart" :key="i" class="checkout-item-card pa-4 mb-4 rounded-xl border" @click="$router.push('/productdetailview/' + item.productId.urlname)" style="cursor: pointer;">
                  <div class="d-flex align-center">
                    <template v-if="item.productId.photos && item.productId.photos.length > 0">
                      <v-img
                        :src="mediaURL + item.productId.photos[0]"
                        class="rounded-lg mr-4 bg-light"
                width="80"
                        max-width="80"
                        height="80"
                        contain
                      ></v-img>
                    </template>
                    <template v-else>
                      <v-img
                        :src="require('../../../assets/noimg.png')"
                        width="80"
                        height="80"
                        contain
                        class="rounded-lg mr-4 bg-light"
                      ></v-img>
                    </template>
                    <div class="flex-grow-1">
                      <div class="d-flex justify-space-between align-start">
                        <h4 class="item-name-preview mb-1">{{ item.productId.productname }}</h4>
                        <div class="d-flex flex-column align-end">
                          <div class="qty-controller d-flex align-center rounded-lg bg-light px-2 py-1 mb-2">
                             <v-btn icon x-small class="qty-btn" @click.stop="editCart(item, item.quantity - 1)">
                               <v-icon size="16">mdi-minus</v-icon>
                             </v-btn>
                             <span class="qty-value mx-4">{{ item.quantity }}</span>
                             <v-btn icon x-small class="qty-btn" @click.stop="editCart(item, item.quantity + 1)">
                               <v-icon size="16">mdi-plus</v-icon>
                             </v-btn>
                          </div>
                          <v-btn icon small color="#ef4444" class="remove-cart-btn" @click.stop="removeCart(item.productId)">
                            <v-icon size="18">mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </div>
                      </div>
                      <div class="d-flex align-center mt-n2">
                        <span class="total-value-small mr-2">₹{{ item.totalOfferPrice }}</span>
                        <span class="old-price-small mr-2">₹{{ item.totalPrice }}</span>
                      </div>
                      <div v-if="item.isPromoApplied" class="promo-applied-tag d-flex align-center mt-1">
                        <v-icon x-small color="#10b981" class="mr-1">mdi-check-decagram</v-icon>
                        <span class="green--text text-caption">Coupon Applied</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-flex>

          <!-- Right Column: Order Summary -->
          <v-flex xs12 lg5 mt-6 mt-lg-0>
            <v-card flat class="summary-card sticky-sidebar pa-8">
              <h2 class="section-subtitle mb-8 pb-2 border-bottom">Price Summary</h2>
              
              <div class="summary-details">
                <div class="d-flex justify-space-between mb-4">
                  <span class="summary-label">Standard Price</span>
                  <span class="summary-value">₹{{ total.totalPrice }}</span>
                </div>
                
                <div class="d-flex justify-space-between mb-4" v-if="total.totalDiscountAmount > 0">
                  <span class="summary-label">Total Discount</span>
                  <span class="summary-value green--text">- ₹{{ total.totalDiscountAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4" v-if="total.totalTaxableAmount !== undefined">
                  <span class="summary-label">Taxable Amount</span>
                  <span class="summary-value">₹{{ total.totalTaxableAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4" v-if="total.totalTaxAmount !== undefined">
                  <span class="summary-label">GST</span>
                  <span class="summary-value">₹{{ total.totalTaxAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4 pl-8" v-if="total.sgstAmount !== undefined">
                  <span class="summary-label">SGST</span>
                  <span class="summary-value">₹{{ total.sgstAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4 pl-8" v-if="total.cgstAmount !== undefined">
                  <span class="summary-label">CGST</span>
                  <span class="summary-value">₹{{ total.cgstAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4" v-if="total.totalPackingCharge > 0">
                  <span class="summary-label">Packing Charges</span>
                  <span class="summary-value">₹{{ total.totalPackingCharge }}</span>
                </div>
                
                <div class="d-flex justify-space-between mb-4" v-if="total.totalDeliveryCharge > 0">
                  <span class="summary-label">Delivery Charges</span>
                  <span class="summary-value">₹{{ total.totalDeliveryCharge }}</span>
                </div>
                
                <v-divider class="my-6"></v-divider>
                
                <div class="d-flex justify-space-between mb-10">
                  <span class="total-label">Payable Amount</span>
                  <div class="text-right">
                    <span class="total-value">₹{{ total.totalOfferPrice }}</span>
                    <p class="tax-inclusive-text">Inclusive of all taxes</p>
                  </div>
                </div>

                <div class="checkout-actions">
                  <v-btn
                    block
                    depressed
                    x-large
                    color="#3EA56E"
                    dark
                    class="place-order-btn py-7 mb-4 rounded-xl"
                    @click="placeorder()"
                  >
                    Confirm & Pay
                  </v-btn>
                  <v-btn
                    block
                    outlined
                    large
                    color="#64748b"
                    class="text-none ibmsemibold rounded-xl"
                    to="/cart"
                  >
                    Return to Cart
                  </v-btn>
                </div>

                <div class="secure-checkout-notice d-flex align-center justify-center mt-6">
                  <v-icon x-small color="#64748b" class="mr-2">mdi-shield-check-outline</v-icon>
                  <span>Secure Checkout powered by Razorpay</span>
                </div>
              </div>
            </v-card>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-container>
  </div>
</template>

<script src="https://checkout.razorpay.com/v1/checkout.js"></script>
<script>
import axios from "axios";

export default {
  data() {
    return {
      cart: [],
      address1: [],
      curItem: null,
      cartCount: null,
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      adddialog: false,
      editdialog: false,
      dialoge: false,
      curId: null,
      flag: null,
      total: [],
      msg: null,
      radios: null,
      orderID: null,
      code1: null,
      codes: [],
      promocodeApplied: null,
      user: {
        country: null,
        state: null,
        district: null,
        firstname: null,
        lastname: null,
        pincode: null,
        streetAddress: null,
        locality: null,
        city: null,
        delphone: null,
        email: null,
      },
      rules: {
        required: (value) => !!value || "Required.",
        min: (v) => v.length >= 8 || "Min 8 characters",
        minPhone: (value) => value.length == 10 || "Invalid Phone",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        },
      },
      phoneRules: [
        (v) => !!v || "phone is required",
        (v) => (v && v.length <= 10) || "number must be less than 11 characters",
        (v) => (v && v.length >= 10) || "number must be greater than 10 characters",
        (v) => /^\d{10}$/.test(v) || "phone number must be a number",
      ],
      pinRules: [
        (v) => !!v || "Pincode is Required",
        (v) => /^(\d{4}|\d{6})$/.test(v) || "Pincode must be valid",
      ],
    };
  },
  mounted() {
    this.getData();
    this.getAddress();
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    appLogin() {
      return this.$store.state.isLoggedIn;
    },
  },
  methods: {
    callPromo(item1) {
      this.code1 = item1;
    },
    applyPromocode() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/Cart/Getlist/productstatus",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          promoCode: this.code1,
          deliveryAddressId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.total = response.data;
          this.cart = response.data.cart;
          this.promocodeApplied = response.data.promoCodeDetails.promoCode;
          this.msg = response.data.msg;
          this.showSnackBar = true;
        })
        .catch((err) => {
          this.appLoading = false;
          this.msg = "Error applying promocode";
          this.showSnackBar = true;
          console.error(err);
        });
    },
    getAddress() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/ordered/deliveryaddress/getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          deliveryaddressId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.user = response.data.deliveryaddress;
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    placeorder() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/customer/placeorder",
        data: {
          deliveryAddressId: this.$route.query.id,
          orderTotal: this.total.totalOfferPrice,
          promoCode: this.code1,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            const PHNO = response.data.phone;
            const id = response.data.id;
            const orderid = response.data.data.order_id;

            const options = {
              key: "rzp_live_ShGFZKpjBK09Gr",
              // key: "rzp_test_ShFpH9VHzFyMxv", 
              amount: Number(this.total.totalOfferPrice * 100),
              name: this.user.firstname,
              currency: "INR",
              description: "KLO6 Checkout",
              image: "https://jolly-volhard-bc2f0b.netlify.com/favicon.ico",
              order_id: orderid,
              handler: function (response) {
                location.href = "/PaywaitScreen/" + id + "/" + orderid;
              },
              prefill: {
                name: this.user.firstname + " " + this.user.lastname,
                email: this.user.email,
                contact: this.user.delphone,
              },
              theme: {
                color: "#3EA56E",
              },
            };
            const rzp1 = new window.Razorpay(options);
            rzp1.open();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    getData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/Cart/Getlist/productstatus",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          deliveryAddressId: this.$route.query.id,
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.cart = response.data.cart;
          this.cartCount = response.data.cart_length;
          this.codes = response.data.availablePromoCodes;
          this.total = response.data;
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
    editCart(item, qty) {
      if (qty < 1) return;
      this.appLoading = true;
      var data = {
        quantity: qty,
        id: item.productId._id,
        type: "cart",
      };
      var headers1 = {};
      if (this.appLogin) {
        headers1 = {
          "x-auth-token": localStorage.getItem("token"),
        };
      } else {
        data["guestid"] = localStorage.getItem("guestId");
      }

      axios({
        method: "post",
        url: "/cart/edit",
        data: data,
        headers: headers1,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error(err);
        });
    },
    removeCart(item) {
      this.appLoading = true;
      var data = {};
      var headers1 = {};
      if (this.appLogin) {
        headers1 = {
          "x-auth-token": localStorage.getItem("token"),
        };
      } else {
        data["guestid"] = localStorage.getItem("guestId");
      }

      axios({
        method: "post",
        url: "/Cart/Remove/" + item._id,
        headers: headers1,
        data: data,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showSnackBar = true;
            this.$store.commit("changeCart", response.data.cartLength);
            this.getData();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.error(err);
        });
    },
  },
};
</script>

<style scoped>
.checkout-page {
  background-color: #f8fafc;
  min-height: 100vh;
}

.page-title {
  font-family: "ibmsemibold", sans-serif;
  font-size: 26px;
  color: #1a202c;
  margin: 0;
}

.title-divider {
  border-color: #3EA56E;
  max-width: 60px;
  border-width: 3px;
  opacity: 1;
}

.section-subtitle {
  font-family: "ibmsemibold", sans-serif;
  font-size: 18px;
  color: #1a202c;
  margin: 0;
}

.confirm-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px !important;
}

.address-confirmation-block {
  background-color: #f0fdf4;
  border: 1px solid #dcfce7;
}

.customer-name {
  font-family: "ibmsemibold", sans-serif;
  font-size: 16px;
  color: #1a202c;
}

.address-text {
  font-family: "sofialight", sans-serif;
  font-size: 14px;
  color: #475569;
}

.coupon-tag {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.coupon-code {
  font-size: 14px;
  color: #3EA56E;
  letter-spacing: 0.5px;
}

.coupon-desc {
  font-family: "sofialight", sans-serif;
  font-size: 12px;
  color: #64748b;
}

.checkout-item-row {
  border-bottom: 1px solid #f1f5f9;
}

.item-name-preview {
  font-family: "ibmmedium", sans-serif;
  font-size: 14px;
  color: #1e293b;
}

.item-qty-preview, .item-price-preview {
  font-family: "ibmmedium", sans-serif;
  font-size: 13px;
  color: #64748b;
}

.checkout-item-card {
  transition: transform 0.2s ease;
  border-color: #f1f5f9 !important;
}

.checkout-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.qty-controller {
  background-color: #f1f5f9;
}

.qty-btn {
  background-color: transparent !important;
  color: #3EA56E !important;
}

.qty-value {
  font-family: "ibmsemibold", sans-serif;
  font-size: 14px;
  color: #1e293b;
}

.remove-cart-btn {
  background-color: #fef2f2 !important;
}

.total-value-small {
  font-family: "ibmsemibold", sans-serif;
  font-size: 16px;
  color: #3EA56E;
}

.old-price-small {
  text-decoration: line-through;
  font-size: 13px;
  color: #94a3b8;
}

.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
}

.sticky-sidebar {
  position: sticky;
  top: 100px;
}

.summary-label {
  font-family: "sofialight", sans-serif;
  font-size: 15px;
  color: #64748b;
}

.summary-value {
  font-family: "ibmmedium", sans-serif;
  font-size: 15px;
  color: #1e293b;
}

.total-label {
  font-family: "ibmsemibold", sans-serif;
  font-size: 18px;
  color: #1a202c;
}

.total-value {
  font-family: "ibmsemibold", sans-serif;
  font-size: 24px;
  color: #3EA56E;
}

.tax-inclusive-text {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

.secure-checkout-notice {
  font-family: "sofialight", sans-serif;
  font-size: 12px;
  color: #64748b;
}

.bb-1 {
  border-bottom: 1px solid #f1f5f9;
}

.bg-light {
  background-color: #f8fafc;
}

::-webkit-scrollbar {
  width: 5px !important;
}
::-webkit-scrollbar-track {
  background: #f1f5f9 !important;
}
::-webkit-scrollbar-thumb {
  background: #3ea56e !important;
  border-radius: 5px !important;
}

@media (max-width: 960px) {
  .sticky-sidebar {
    position: static;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 22px;
  }
  .total-value {
    font-size: 20px;
  }
}
</style>